import React, { useEffect, useRef, useState } from 'react';

// A listvy card's floor plan: a small tile in the photo's bottom-right corner, cropped to the drawing
// (plans come with wide white margins and a title block). Several plans stack behind it like a deck.
// Hover grows it to a square that shows the whole plan; on touch screens a long press does the same until the
// finger lifts. A click (or tap) is the card's: it opens the listing as usual.
const getPlanUrls = (listing) => (Array.isArray(listing?.planritning) ? listing.planritning : [])
    .map((entry) => String((entry && typeof entry === 'object' ? entry.url : entry) || '').trim())
    .filter(Boolean);

// How long a press must last before the plan grows, and how far the finger may move (a swipe or a scroll
// cancels it).
const LONG_PRESS_MS = 350;
const LONG_PRESS_MOVE_PX = 10;

const ListingCardFloorplan = ({ listing, swedish = true }) => {
    const [pressOpen, setPressOpen] = useState(false);
    const pressRef = useRef({ timer: null, x: 0, y: 0, opened: false, suppressClickUntil: 0 });

    const cancelPress = () => {
        window.clearTimeout(pressRef.current.timer);
        pressRef.current.timer = null;
    };
    useEffect(() => () => window.clearTimeout(pressRef.current.timer), []);

    const plans = getPlanUrls(listing);
    if (!plans.length) return null;
    const count = plans.length;
    const label = count > 1
        ? (swedish ? `${count} planritningar` : `${count} floor plans`)
        : (swedish ? 'Planritning' : 'Floor plan');
    return (
        <button
            type="button"
            className={`listing-card-floorplan${count > 1 ? ' is-deck' : ''}${pressOpen ? ' is-open' : ''}`}
            onClick={(event) => {
                // The click that ends a long press only closes the plan; any other click reaches the card.
                if (Date.now() < pressRef.current.suppressClickUntil) event.stopPropagation();
            }}
            onTouchStart={(event) => {
                const touch = event.touches[0];
                cancelPress();
                pressRef.current = { timer: null, x: touch.clientX, y: touch.clientY, opened: false, suppressClickUntil: 0 };
                pressRef.current.timer = window.setTimeout(() => {
                    pressRef.current.timer = null;
                    pressRef.current.opened = true;
                    setPressOpen(true);
                }, LONG_PRESS_MS);
            }}
            onTouchMove={(event) => {
                const touch = event.touches[0];
                const moved = Math.hypot(touch.clientX - pressRef.current.x, touch.clientY - pressRef.current.y);
                if (moved > LONG_PRESS_MOVE_PX) cancelPress();
            }}
            onTouchEnd={(event) => {
                cancelPress();
                if (pressRef.current.opened) {
                    // No click (and no listing popup) after a long press.
                    event.preventDefault();
                    pressRef.current.opened = false;
                    pressRef.current.suppressClickUntil = Date.now() + 600;
                    setPressOpen(false);
                }
            }}
            onTouchCancel={() => {
                cancelPress();
                pressRef.current.opened = false;
                setPressOpen(false);
            }}
            onContextMenu={(event) => event.preventDefault()}
            aria-label={label}
        >
            {count > 2 && <span className="listing-card-floorplan-sheet is-third" aria-hidden="true" />}
            {count > 1 && <span className="listing-card-floorplan-sheet is-second" aria-hidden="true" />}
            <span
                className="listing-card-floorplan-image"
                aria-hidden="true"
                style={{ backgroundImage: `url("${plans[0]}")` }}
            />
            <span
                className="listing-card-floorplan-image is-whole"
                aria-hidden="true"
                style={{ backgroundImage: `url("${plans[0]}")` }}
            />
            <span className="listing-card-floorplan-tag">{label}</span>
        </button>
    );
};

export default ListingCardFloorplan;
